import React, { useState, useEffect } from "react";
import {
  User,
  Mail,
  Phone,
  FileText,
  Camera,
  Save,
  Building2,
  X
} from "lucide-react";
import toast, { Toaster } from "react-hot-toast";

const Manager = ({ residenceId }) => {
  const [manager, setManager] = useState({
    name: "",
    email: "",
    phone: "",
    bio: "",
    image: ""
  });

  const [originalManager, setOriginalManager] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch manager data from backend
  useEffect(() => {
    if (!residenceId) return;

    const fetchManager = async () => {
      try {
        const res = await fetch(
          `https://https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/manager/residence/${residenceId}`
        );
        if (!res.ok) throw new Error("Failed to fetch manager");
        const data = await res.json();

        setManager({
          name: data.name || "",
          email: data.email || "",
          phone: data.phone || "",
          bio: data.bio || "",
          image: data.image || "uploads/managers/default.png"
        });
        setOriginalManager(data);
      } catch (err) {
        console.error("Error fetching manager:", err);
        toast.error("Failed to load manager data");
      }
    };

    fetchManager();
  }, [residenceId]);

  // Handle image upload
  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const toastId = toast.loading("Uploading image...");

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await fetch("https://https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/manager/upload", {
        method: "POST",
        body: formData,
      });

      if (!res.ok) throw new Error("Failed to upload image");

      const data = await res.json();
      setManager((prev) => ({ ...prev, image: data.path }));
      toast.success("Image uploaded successfully!", { id: toastId });
    } catch (err) {
      console.error("Image upload error:", err);
      toast.error("Failed to upload image", { id: toastId });
    }
  };

  // Save updated info to backend
  const handleSave = async () => {
    if (!residenceId || !originalManager?.id) return;

    const toastId = toast.loading("Saving manager info...");

    try {
      setIsSaving(true);

      const payload = {
        id: originalManager.id,
        residenceId: residenceId,
        name: manager.name,
        email: manager.email,
        phone: manager.phone,
        bio: manager.bio,
        image: manager.image
      };

      const res = await fetch(
        `https://https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/api/manager/residence/${residenceId}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload)
        }
      );

      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Failed to update manager: ${text}`);
      }

      setOriginalManager({ ...payload });
      toast.success("Manager information updated successfully!", { id: toastId });
    } catch (err) {
      console.error("Update error:", err);
      toast.error(`Failed to update manager: ${err.message}`, { id: toastId });
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel changes
  const handleCancel = () => {
    if (originalManager) {
      setManager(originalManager);
      toast("Changes reverted", { icon: "↩️" });
    }
  };

  // Determine image URL to display
  const getImageUrl = () => {
    if (!manager.image) return "https://https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/uploads/managers/default.png";
    return manager.image.startsWith("uploads/")
      ? `https://https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/${manager.image}`
      : manager.image;
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <Toaster position="top-right" />

      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gray-100 rounded-lg">
            <Building2 className="w-5 h-5 text-gray-500" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-700">Manager Settings</h2>
            <p className="text-sm text-gray-500 mt-0.5">Update residence manager information and contact details</p>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3">
        {/* LEFT PANEL – Profile */}
        <div className="border-r border-gray-200 p-6 bg-gray-50">
          <div className="flex flex-col items-center text-center">
            <div className="relative w-28 h-28 rounded-full overflow-hidden border-2 border-gray-200 bg-white shadow-sm">
              <img
                src={getImageUrl()}
                alt="Manager"
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = "https://https://msarestapis-acckcpbyeshpa3f3.southafricanorth-01.azurewebsites.net/uploads/managers/default.png";
                }}
              />
              <label className="absolute bottom-0 right-0 bg-white border border-gray-200 rounded-full p-1.5 cursor-pointer hover:bg-gray-50 transition shadow-sm">
                <Camera size={12} className="text-gray-500" />
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImageUpload}
                />
              </label>
            </div>

            <h3 className="mt-4 text-base font-semibold text-gray-700">{manager.name || "Not Assigned"}</h3>
            <p className="text-sm text-gray-500">Residence Manager</p>

            <div className="mt-5 w-full pt-4 border-t border-gray-200">
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 justify-center text-gray-500">
                  <Mail className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-xs text-gray-600">{manager.email || "No email provided"}</span>
                </div>
                <div className="flex items-center gap-2 justify-center text-gray-500">
                  <Phone className="w-3.5 h-3.5 text-gray-400" />
                  <span className="text-xs text-gray-600">{manager.phone || "No phone provided"}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* RIGHT PANEL – Editable Form */}
        <div className="lg:col-span-2 p-6">
          <div>
            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Personal Information
            </h4>
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Full Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={manager.name}
                    onChange={(e) => setManager((prev) => ({ ...prev, name: e.target.value }))}
                    className="w-full pl-10 pr-3 py-2 text-sm rounded-lg border border-gray-200 focus:border-gray-400 focus:ring-1 focus:ring-gray-400 outline-none transition-all text-gray-700 placeholder-gray-400"
                    placeholder="Enter full name"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Email Address</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="email"
                    value={manager.email}
                    onChange={(e) => setManager((prev) => ({ ...prev, email: e.target.value }))}
                    className="w-full pl-10 pr-3 py-2 text-sm rounded-lg border border-gray-200 focus:border-gray-400 focus:ring-1 focus:ring-gray-400 outline-none transition-all text-gray-700 placeholder-gray-400"
                    placeholder="Enter email address"
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Contact Number</label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={manager.phone}
                    onChange={(e) => setManager((prev) => ({ ...prev, phone: e.target.value }))}
                    className="w-full pl-10 pr-3 py-2 text-sm rounded-lg border border-gray-200 focus:border-gray-400 focus:ring-1 focus:ring-gray-400 outline-none transition-all text-gray-700 placeholder-gray-400"
                    placeholder="Enter phone number"
                  />
                </div>
              </div>

              {/* Bio */}
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Biography</label>
                <div className="relative">
                  <FileText className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <textarea
                    rows="4"
                    value={manager.bio}
                    onChange={(e) => setManager((prev) => ({ ...prev, bio: e.target.value }))}
                    className="w-full pl-10 pr-3 py-2 text-sm rounded-lg border border-gray-200 focus:border-gray-400 focus:ring-1 focus:ring-gray-400 outline-none transition-all text-gray-700 placeholder-gray-400 resize-none"
                    placeholder="Enter biography information"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Buttons */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <button
              onClick={handleCancel}
              className="inline-flex items-center gap-2 px-4 py-2 text-xs font-medium rounded-lg border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition-all duration-200"
            >
              <X className="w-3.5 h-3.5" />
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="inline-flex items-center gap-2 px-4 py-2 text-xs font-medium rounded-lg bg-gray-800 text-white hover:bg-gray-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
            >
              <Save className="w-3.5 h-3.5" />
              {isSaving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Manager;